<template>
  <header
    data-shell="top-bar"
    class="hidden h-12 shrink-0 grid-cols-[minmax(0,1fr)_minmax(180px,380px)_minmax(max-content,1fr)] items-center gap-4 px-4 lg:grid">
    <div class="flex min-w-0 items-center gap-2">
      <a v-if="user?.is_admin" :href="adminUrl" class="mr-1.5 shrink-0" title="Admin">
        <img class="size-[22px]" src="/img/rm-logomark.svg" alt="RightMessage" />
      </a>
      <router-link v-else :to="dashboardRoute" class="mr-1.5 shrink-0" title="Dashboard">
        <img class="size-[22px]" src="/img/rm-logomark.svg" alt="RightMessage" />
      </router-link>

      <TeamDropdown variant="shell" class="min-w-0 shrink" />
      <template v-if="pageLabel">
        <span class="text-lg font-light text-line-strong" aria-hidden="true">/</span>
        <RecentPages :label="pageLabel" />
      </template>
    </div>
    <button
      type="button"
      class="flex h-8 w-full cursor-pointer items-center gap-2.5 rounded-lg border border-line bg-white pr-2 pl-3 text-[13px] text-muted dark:bg-panel"
      @click="openCommandPalette()">
      <SearchIcon class="size-[15px] shrink-0" aria-hidden="true" />
      <span class="flex-1 truncate text-left">Search or jump to…</span>
      <kbd class="rounded border border-line px-[5px] py-px text-[11px] font-medium">{{ modifierKeySymbol }}K</kbd>
    </button>

    <div class="flex min-w-0 items-center justify-end gap-1">
      <router-link
        v-if="dormant"
        :to="{ name: ROUTES.DASHBOARD, hash: '#recommended' }"
        class="mr-2 inline-flex items-center gap-2 rounded-lg border border-danger bg-danger/10 px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-danger"
        @click="jumpToRecovery">
        <span class="size-1.5 rounded-full bg-danger" aria-hidden="true" />
        No visitors in 7 days · Check the snippet
      </router-link>
      <DataStatusIndicator v-if="!isDemoMode && !dormant" />
      <button
        v-if="rightyAvailable"
        type="button"
        :aria-expanded="rightyStore.isDrawerOpen"
        @click="toggleRighty"
        class="flex h-[30px] cursor-pointer items-center rounded-md px-2 text-[13px] font-medium whitespace-nowrap text-ink underline decoration-line-strong underline-offset-3 hover:bg-tint hover:text-primary hover:decoration-current">
        Need help?
      </button>
      <a
        v-else
        href="https://rightmessage.com/help"
        target="_blank"
        rel="noopener noreferrer"
        class="flex h-[30px] cursor-pointer items-center rounded-md px-2 text-[13px] font-medium whitespace-nowrap text-ink underline decoration-line-strong underline-offset-3 hover:bg-tint hover:text-primary hover:decoration-current">
        Need help?
      </a>
    </div>
  </header>

  <header class="flex h-12 shrink-0 items-center gap-2 px-3 lg:hidden">
    <button
      type="button"
      class="flex size-[30px] items-center justify-center rounded-md text-ink hover:bg-tint"
      aria-label="Open navigation"
      @click="mobileNavVisible = true">
      <MenuIcon class="size-5" aria-hidden="true" />
    </button>
    <span class="min-w-0 flex-1 truncate text-[13px] font-semibold text-heading">{{ pageLabel }}</span>
    <button
      type="button"
      class="flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md text-ink hover:bg-tint"
      aria-label="Search, create, or ask Righty"
      @click="openCommandPalette()">
      <SearchIcon class="size-5" aria-hidden="true" />
    </button>
    <button
      v-if="rightyAvailable"
      type="button"
      :aria-expanded="rightyStore.isDrawerOpen"
      class="flex h-[30px] shrink-0 cursor-pointer items-center rounded-md px-1.5 text-[13px] font-medium whitespace-nowrap text-ink underline decoration-line-strong underline-offset-3 hover:bg-tint hover:text-primary hover:decoration-current"
      @click="toggleRighty">
      Need help?
    </button>
    <a
      v-else
      href="https://rightmessage.com/help"
      target="_blank"
      rel="noopener noreferrer"
      class="flex h-[30px] shrink-0 cursor-pointer items-center rounded-md px-1.5 text-[13px] font-medium whitespace-nowrap text-ink underline decoration-line-strong underline-offset-3 hover:bg-tint hover:text-primary hover:decoration-current">
      Need help?
    </a>
    <Drawer v-model:visible="mobileNavVisible" class="bg-overlay! dark:bg-canvas!">
      <template #header>
        <div class="flex min-w-0 items-center gap-2">
          <img class="size-[22px] shrink-0" src="/img/rm-logomark.svg" alt="RightMessage" />
          <TeamDropdown variant="shell" class="min-w-0" />
        </div>
      </template>
      <Sidebar />
    </Drawer>
  </header>
</template>

<script setup lang="ts">
import { shellIcons } from '@dashboard/components/icons/shellIcons'
import DataStatusIndicator from '@dashboard/components/common/DataStatusIndicator.vue'
import TeamDropdown from '@dashboard/components/common/TeamDropdown.vue'
import RecentPages from '@dashboard/components/layouts/RecentPages.vue'
import Sidebar from '@dashboard/components/layouts/Sidebar.vue'
import { useCommandPalette } from '@dashboard/composables/useCommandPalette'
import { getTrackedRouteLabel } from '@dashboard/composables/useRecentPages'
import { ROUTES } from '@dashboard/router/routeNames'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useBillingDialogStore } from '@dashboard/stores/billingDialogStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useDashboardSetup } from '@dashboard/modules/dashboard/services/dashboardSetup'
import { useRightyStore } from '@dashboard/modules/righty/stores/rightyStore'
import { backendUrl } from '@dashboard/utils/backendUrl'
import { modifierKeySymbol } from '@dashboard/utils/modifierKey'
import { storeToRefs } from 'pinia'
import Drawer from 'primevue/drawer'
import { computed, inject, ref, unref, watch } from 'vue'
import { useRoute } from 'vue-router'

const { menu: MenuIcon, search: SearchIcon } = shellIcons

withDefaults(defineProps<{ rightyAvailable?: boolean }>(), { rightyAvailable: true })

const route = useRoute()
const setup = useDashboardSetup()
const rightyStore = useRightyStore()
async function toggleRighty() {
  if (rightyStore.isDrawerOpen) {
    rightyStore.closeDrawer()
    return
  }
  await rightyStore.startNewConversation()
  rightyStore.openDrawer()
}
const dormant = computed(() => !setup.isError.value && setup.data.value?.health.state === 'dormant')
function jumpToRecovery() {
  if (route.name === ROUTES.DASHBOARD) {
    document.getElementById('recommended')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const { user } = storeToRefs(useAuthStore())
const { owner } = storeToRefs(useConfigStore())

const isDemoMode = computed(() => unref(inject('isDemo', false)))
const { open: openCommandPalette } = useCommandPalette()

const mobileNavVisible = ref(false)
watch(
  () => route.fullPath,
  () => {
    mobileNavVisible.value = false
  }
)
// The drawer's sidebar trial status opens the billing dialog; don't leave the drawer trapping focus behind it.
const billingDialog = useBillingDialogStore()
watch(
  () => billingDialog.visible,
  (visible) => {
    if (visible) {
      mobileNavVisible.value = false
    }
  }
)

const dashboardRoute = computed(() =>
  route.params.teamPid ? { name: ROUTES.DASHBOARD, params: { teamPid: route.params.teamPid } } : '/'
)

const pageLabel = computed(() => getTrackedRouteLabel(route) ?? route.meta.title ?? null)

const adminUrl = computed(() => {
  const manageUrl = backendUrl('/manage')
  if (!owner.value || owner.value.email === user.value?.email) {
    return manageUrl
  }
  return manageUrl + `/users/${owner.value.id}`
})
</script>
